import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { TipTargeting } from '../utils/tipTargeting';
import ContentEditor from './ContentEditor';
import AudioPlayer from './AudioPlayer';
import '../styles/tips.css';

const OffScoreTipDialog = ({ 
  isOpen, 
  onClose, 
  onCreateTip,
  editingTip = null,
  currentFileName = null
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    type: 'off-score',
    content: '',
    contentHtml: '',
    targetType: 'everyone', // 'everyone', 'specific', 'roles'
    targetUserIds: [],
    targetRoles: [],
    musicxmlId: null
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [showContentEditor, setShowContentEditor] = useState(false);
  const [audioClips, setAudioClips] = useState([]);
  const dialogRef = useRef(null);

  // Load users when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (editingTip) {
        // Populate form with editing tip data
        const targetType = editingTip.target_rules?.everyone ? 'everyone' :
                          editingTip.target_rules?.roles?.length > 0 ? 'roles' : 'specific';
        
        setFormData({
          type: 'off-score',
          content: editingTip.content,
          contentHtml: editingTip.content_html || editingTip.content,
          targetType: targetType,
          targetUserIds: editingTip.target_user_ids || [],
          targetRoles: editingTip.target_rules?.roles || [],
          musicxmlId: editingTip.musicxml_id || null
        });
      } else {
        // New off-score tip
        setFormData({
          type: 'off-score',
          content: '',
          contentHtml: '',
          targetType: 'everyone',
          targetUserIds: [],
          targetRoles: [],
          musicxmlId: null
        });
      }
      
      setError(null);
      loadUsers();
      loadFiles();
      loadAudioClips();
    }
  }, [isOpen, editingTip]);

  // Reload audio clips when content changes
  useEffect(() => {
    if (isOpen) {
      loadAudioClips();
    }
  }, [formData.content, formData.contentHtml, isOpen]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=getAllUsers&userId=${user.id}`);
      
      if (response.ok) {
        const userData = await response.json();
        // console.log('OffScoreTipDialog: Loaded users:', userData);
        setUsers(userData.users || []);
      } else {
        console.error('Failed to load users, response not ok:', response.status);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadFiles = async () => {
    setLoadingFiles(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/musicxml?operation=list`);
      const data = await response.json();
      setAvailableFiles(data.files || []);
    } catch (error) {
      console.error('Failed to load available files:', error);
      setAvailableFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadAudioClips = async () => {
    try {
      // Extract audio clip names from the current content
      const contentToScan = formData.contentHtml || formData.content || '';
      const audioRegex = /\[audio:([^\]]+)\]/g;
      const clipNames = [];
      let match;
      
      while ((match = audioRegex.exec(contentToScan)) !== null) {
        const clipName = match[1];
        if (!clipNames.includes(clipName)) {
          clipNames.push(clipName);
        }
      }
      
      if (clipNames.length === 0) {
        setAudioClips([]);
        return;
      }
      
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        operation: 'getAudioClipsByNames',
        names: clipNames.join(',')
      });
      
      const response = await fetch(`${API_BASE}/api/audio-clips?${params}`);
      
      if (response.ok) {
        const clipsData = await response.json();
        setAudioClips(clipsData);
      }
    } catch (err) {
      console.error('Failed to load audio clips for tip preview:', err);
    }
  };

  // Function to render content with embedded audio players
  const renderContentWithAudio = (content) => {
    if (!content) return null;
    
    const audioRegex = /\[audio:([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = audioRegex.exec(content)) !== null) {
      // Add content before the audio markup
      if (match.index > lastIndex) {
        const beforeText = content.substring(lastIndex, match.index);
        parts.push(
          <span key={`text-${key++}`}>
            {beforeText}
          </span>
        );
      }
      
      // Add audio player
      const clipName = match[1];
      const clip = audioClips.find(c => c.name === clipName);
      parts.push(
        <div key={`audio-${key++}`} style={{ margin: '8px 0' }}>
          {clip ? (
            <AudioPlayer
              clipName={clip.name}
              fileUrl={clip.file_url}
              compact={true}
              showTitle={true}
            />
          ) : (
            <div style={{
              padding: '6px 10px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '4px',
              color: '#dc2626',
              fontSize: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              ⚠️ Audio "{clipName}" not found
            </div>
          )}
        </div>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining content
    if (lastIndex < content.length) {
      const remainingText = content.substring(lastIndex);
      parts.push(
        <span key={`text-${key++}`}>
          {remainingText}
        </span>
      );
    }
    
    // If no audio markup found, just render the content normally
    if (parts.length === 0) {
      return <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div>;
    }
    
    return <div style={{ whiteSpace: 'pre-wrap' }}>{parts}</div>;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      setError(t('tips.contentRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare target rules
      let targetUserIds = [];
      let targetRules = {};

      switch (formData.targetType) {
        case 'everyone':
          targetRules.everyone = true;
          break;
        case 'specific':
          targetUserIds = formData.targetUserIds;
          break;
        case 'roles':
          targetRules.roles = formData.targetRoles;
          break;
      }

      const tipData = {
        type: 'off-score',
        content: formData.content.trim(),
        contentHtml: formData.contentHtml || formData.content.trim(),
        creatorUserId: user.id,
        targetUserIds,
        targetRules,
        scorePosition: null, // Off-score tips don't have positions
        musicxmlId: formData.musicxmlId // Can be null for general tips or specific file
      };

      await onCreateTip(tipData);
      onClose();
    } catch (err) {
      setError(err.message || t('tips.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      targetUserIds: prev.targetUserIds.includes(userId)
        ? prev.targetUserIds.filter(id => id !== userId)
        : [...prev.targetUserIds, userId]
    }));
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      targetRoles: prev.targetRoles.includes(role)
        ? prev.targetRoles.filter(r => r !== role)
        : [...prev.targetRoles, role]
    }));
  };

  const handleContentSave = (contentData) => {
    // Extract both text and HTML content from the ContentEditor data
    setFormData(prev => ({
      ...prev,
      content: contentData.contentText,
      contentHtml: contentData.contentHtml || contentData.contentText
    }));
    setShowContentEditor(false);
  };

  if (!isOpen) return null;

  return (
    <div className="tip-modal-overlay">
      <div 
        ref={dialogRef}
        className="tip-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          background: '#065f46',
          color: '#f9fafb',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #374151',
          flexShrink: 0
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            💬 {editingTip ? t('tips.editOffScoreTip') : t('tips.createOffScoreTip')}
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#d1d5db',
              fontSize: '24px',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Content */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#e5e7eb',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {t('tips.content')} *
              </label>
              
              <div style={{
                border: '1px solid #4b5563',
                borderRadius: '6px',
                padding: '12px',
                background: '#374151',
                minHeight: '100px'
              }}>
                {formData.content ? (
                  <div style={{
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: '#f9fafb'
                  }}>
                    {renderContentWithAudio(formData.content)}
                  </div>
                ) : (
                  <div style={{
                    color: '#9ca3af',
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    {t('tips.contentPlaceholder')}
                  </div>
                )}
              </div>
              
              <button
                type="button"
                onClick={() => setShowContentEditor(true)}
                style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📝 {formData.content ? 'Edit Content' : 'Write Content'}
              </button>
            </div>

            {/* File Association */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#e5e7eb',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '12px'
              }}>
                {t('tips.associatedFile')}
              </label>

              <div style={{ marginBottom: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  color: '#d1d5db'
                }}>
                  <input
                    type="radio"
                    name="fileAssociation"
                    value=""
                    checked={!formData.musicxmlId}
                    onChange={() => setFormData(prev => ({ ...prev, musicxmlId: null }))}
                    style={{ marginRight: '8px' }}
                  />
                  🌐 {t('tips.allSongs')}
                </label>
                
                {currentFileName && (
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    color: '#d1d5db'
                  }}>
                    <input
                      type="radio"
                      name="fileAssociation"
                      value={currentFileName}
                      checked={formData.musicxmlId === currentFileName}
                      onChange={() => setFormData(prev => ({ ...prev, musicxmlId: currentFileName }))}
                      style={{ marginRight: '8px' }}
                    />
                    🎼 {t('tips.currentFile')}: {currentFileName}
                  </label>
                )}

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  color: '#d1d5db'
                }}>
                  <input
                    type="radio"
                    name="fileAssociation"
                    value="specific"
                    checked={formData.musicxmlId && formData.musicxmlId !== currentFileName}
                    onChange={() => {}} // Handled by file selection below
                    style={{ marginRight: '8px' }}
                  />
                  📁 {t('tips.specificFile')}
                </label>
              </div>

              {/* File Selection Dropdown */}
              {(formData.musicxmlId && formData.musicxmlId !== currentFileName) || (!formData.musicxmlId && availableFiles.length > 0) ? (
                <div style={{
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  background: '#374151',
                  marginTop: '8px'
                }}>
                  <select
                    value={formData.musicxmlId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, musicxmlId: e.target.value || null }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: 'none',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '14px'
                    }}
                  >
                    <option value="" style={{ background: '#374151', color: '#f9fafb' }}>{t('tips.selectFile')}</option>
                    {availableFiles.map((file, index) => (
                      <option key={index} value={file.filename} style={{ background: '#374151', color: '#f9fafb' }}>
                        {file.filename} ({(file.size / 1024).toFixed(1)} KB)
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {loadingFiles && (
                <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
                  {t('tips.loadingFiles')}
                </div>
              )}
            </div>

            {/* Target Audience */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#e5e7eb',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '12px'
              }}>
                {t('tips.targetAudience')}
              </label>

              {/* Target Type Selection */}
              <div style={{ marginBottom: '16px' }}>
                {['everyone', 'specific', 'roles'].map(type => (
                  <label key={type} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    color: '#d1d5db'
                  }}>
                    <input
                      type="radio"
                      name="targetType"
                      value={type}
                      checked={formData.targetType === type}
                      onChange={(e) => setFormData(prev => ({ ...prev, targetType: e.target.value }))}
                      style={{ marginRight: '8px' }}
                    />
                    {t(`tips.target${type.charAt(0).toUpperCase() + type.slice(1)}`)}
                  </label>
                ))}
              </div>

              {/* Specific Users */}
              {formData.targetType === 'specific' && (
                <div style={{
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '12px',
                  background: '#374151',
                  maxHeight: '150px',
                  overflow: 'auto'
                }}>
                  {loadingUsers ? (
                    <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('loading')}</p>
                  ) : users.length === 0 ? (
                    <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('tips.noUsers')}</p>
                  ) : (
                    users.map(user => (
                      <label key={user.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        color: '#d1d5db',
                        fontSize: '14px'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.targetUserIds.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          style={{ marginRight: '8px' }}
                        />
                        {user.email}
                      </label>
                    ))
                  )}
                </div>
              )}

              {/* Roles */}
              {formData.targetType === 'roles' && (
                <div style={{
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '12px',
                  background: '#374151'
                }}>
                  {TipTargeting.AVAILABLE_ROLES.map(role => (
                    <label key={role} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      color: '#d1d5db',
                      fontSize: '14px'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.targetRoles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                        style={{ marginRight: '8px' }}
                      />
                      {t(`auth.roles.${role}`)}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '16px',
              borderTop: '1px solid #374151'
            }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: '#374151',
                  color: '#d1d5db',
                  border: '1px solid #4b5563',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || !formData.content.trim()}
                style={{
                  background: loading || !formData.content.trim() ? '#6b7280' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading || !formData.content.trim() ? 'not-allowed' : 'pointer',
                  opacity: loading || !formData.content.trim() ? 0.6 : 1
                }}
              >
                {loading ? t('saving') : (editingTip ? t('update') : t('create'))}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Content Editor Modal */}
      <ContentEditor
        isOpen={showContentEditor}
        onClose={() => setShowContentEditor(false)}
        onSave={handleContentSave}
        contentType="tip"
        editingContent={formData.content ? {
          content_text: formData.content,
          content_html: formData.contentHtml || formData.content
        } : null}
        showScheduling={false}
        showVisibility={false}
        showAudioSupport={true}
        musicxmlId={formData.musicxmlId}
        placeholder="Write your tip content here... You can embed audio clips using the Insert Audio button."
        categories={[]} // No categories for tips
      />
    </div>
  );
};

export default OffScoreTipDialog;